import { ApiFetch, GetPathPrefix } from './api';

function UrlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function Uint8ArraysEqual(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function SubscriptionMatchesVapid(subscription, vapidPublicKey) {
  const key = subscription?.options?.applicationServerKey;
  if (!key) return true;
  return Uint8ArraysEqual(
    new Uint8Array(key),
    UrlBase64ToUint8Array(vapidPublicKey),
  );
}

export function ChatClientPath() {
  const prefix = GetPathPrefix();
  return `${prefix}/chat` || '/chat';
}

export function PushSupported() {
  return (
    typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
  );
}

export async function RegisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  const prefix = GetPathPrefix();
  const swPath = `${prefix}/sw.js`;
  const registration = await navigator.serviceWorker.register(swPath, {
    scope: `${prefix}/` || '/',
  });
  await navigator.serviceWorker.ready;
  return registration;
}

/** Local OS alert when Funhouse is open but another tab/app has focus. */
export function NotifyIfTabHidden({ title, body, tag = 'riddler-funhouse' } = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;
  if (!document.hidden) return false;
  if (!('Notification' in window) || Notification.permission !== 'granted') return false;
  try {
    const note = new Notification(title || "Riddler's Funhouse", {
      body: body || '',
      tag,
      renotify: true,
      icon: `${GetPathPrefix()}/icon-192.png`,
    });
    note.onclick = () => {
      window.focus();
      note.close();
    };
    return true;
  } catch {
    return false;
  }
}

export async function PushPermissionState() {
  if (!PushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'granted') {
    try {
      const registration = await RegisterServiceWorker();
      const sub = await registration?.pushManager?.getSubscription();
      return sub ? 'subscribed' : 'granted';
    } catch {
      return 'granted';
    }
  }
  return 'default';
}

/**
 * Ensure browser subscription matches server VAPID and is stored server-side.
 * UI "Alerts on" alone is not enough: iOS keeps a local sub even when the API
 * never received it or Apple rejects the JWT subject.
 */
export async function SyncPushSubscription({ sendTest = false } = {}) {
  if (!PushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }
  if (Notification.permission !== 'granted') {
    throw new Error('Notification permission was not granted');
  }

  const vapid = await ApiFetch('/push/vapid-public-key');
  if (!vapid.configured || !vapid.public_key) {
    throw new Error('Push is not configured on the server yet (missing VAPID keys)');
  }

  const registration = await RegisterServiceWorker();
  if (!registration?.pushManager) {
    throw new Error('Push manager unavailable');
  }

  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !SubscriptionMatchesVapid(subscription, vapid.public_key)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: UrlBase64ToUint8Array(vapid.public_key),
    });
  }

  await ApiFetch('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ subscription: subscription.toJSON() }),
  });

  if (sendTest) {
    await ApiFetch('/push/test', { method: 'POST' });
  }

  return subscription;
}

export async function EnablePushNotifications() {
  if (!PushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted');
  }

  return SyncPushSubscription({ sendTest: true });
}
